
import { mapState, mapWritableState } from 'pinia'
import { Component, Prop, Vue } from 'vue-property-decorator'

import LButton from '@/components/core/Button.vue'
import NotificationMenu from '@/components/notification/NotificationMenu.vue'
import UserSettings from '@/components/user/settings/UserSettings.vue'
import NewTripModal from '@/components/home/newTrip/NewTripModal.vue'
import JoinTripModal from '@/components/JoinTripModal.vue'
import UserDropdown from '@/components/navbar/UserDropdown.vue'
import PromoProButton from '@/components/navbar/PromoProButton.vue'
import GetProButton from '@/components/navbar/GetProButton.vue'
import { eventModule, authModule, tripModule } from '@/store'
import { logEvent } from '@/utils/utility-manager'
import { getLastActiveOrganization } from '@/utils/auth'
import { useItineraryStore } from '@/stores/itinerary'

@Component({
  name: 'Navbar',
  components: {
    UserSettings,
    JoinTripModal,
    NewTripModal,
    UserDropdown,
    NotificationMenu,
    LButton,
    PromoProButton,
    GetProButton,
  },
  computed: {
    ...mapWritableState(useItineraryStore, ['showItinerary']),
    ...mapState(useItineraryStore, {
      itineraryItemCount: (state) => state.groupedItems.length,
    }),
  },
})
class Navbar extends Vue {
  @Prop({ type: Boolean, default: false })
  readonly enablePromoProButton!: boolean

  logoIsLoaded = false

  showItinerary!: boolean

  itineraryItemCount!: number

  get user() {
    return authModule.user
  }

  get isLoggedIn(): boolean {
    return authModule.loggedIn && authModule.user.accountType !== 'anonymous'
  }

  get trip() {
    return tripModule.trip
  }

  get hasArchive(): boolean {
    return tripModule.hasArchive
  }

  get showArchiveButton(): boolean {
    return (
      (this.hasArchive || this.showArchive) &&
      !this.showTripNavigation &&
      !this.isDiscoverRoute
    )
  }

  get showToggleTripItinerary() {
    return this.$route.name?.includes('trip-id-conversation')
  }

  get showNavigationDrawer(): boolean {
    return eventModule.navigationDrawer
  }

  set showNavigationDrawer(value: boolean) {
    eventModule.toggleNavigationDrawer(value)
  }

  get showTripNavigation(): string {
    return this.$route.params.id
  }

  get showArchive(): boolean {
    return this.$route.name === 'archive'
  }

  get isDiscoverRoute(): boolean {
    if (!this.$route.name) return false
    return this.$route.name.startsWith('discover')
  }

  get discoverSearchModal(): boolean {
    return eventModule.discoverSearchModal
  }

  get hasExtension() {
    return !!this.$slots.extension
  }

  get showNewTripButton() {
    if (this.user.accountType === 'anonymous') {
      return false
    }

    if (!Array.isArray(this.user?.memberships)) {
      return false
    }
    const organization = getLastActiveOrganization(this.user as any)
    // Only show the New Trip button for users in the default Lambus org
    return organization.default
  }

  navArchive(): void {
    this.$router.push({ name: this.showArchive ? 'trips' : 'archive' })
  }

  navDiscover(): void {
    logEvent('inspiration_search_started', {
      context: 'signed_in',
    })
    this.$router.push({ name: 'discover' })
  }

  navBookmarks(): void {
    this.$router.push({ name: 'discover-bookmarks' })
  }

  showJoinTripPrompt(): void {
    logEvent('trip_join_started', {
      context: 'signed_in',
    })

    const dialog = this.$refs.joinTripModal as unknown as InstanceType<
      typeof JoinTripModal
    >
    setTimeout(() => {
      if (dialog) dialog.toggleDialog()
    })
  }

  showNewTripModal(): void {
    const dialog = this.$refs.newTripModal as NewTripModal
    setTimeout(() => {
      if (dialog) dialog.toggleDialog()
    })
  }

  showDiscoverSearchModal(): void {
    eventModule.setDiscoverSearchModal(true)
  }
}

export default Navbar
